import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type DragEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { zeileOffen, type KlappModus } from '@/components/klappmodus'
import { RAND_ZEIGER, useZeileGreifen } from '@/components/ziehen/ZeilenZiehen'
import { FeldStatus, GruppenBereich, type Gruppe } from '@/lib/speichern'

/**
 * **Zeilenblock** — die Erfassung von Stückliste, Verkauf und Verbrauch direkt in
 * der Liste (Auftraggeber 18.9.2026).
 *
 * Jede Zeile ist ein Block aus Eingabefeldern. Gespeichert wird, wenn der Fokus
 * den Block verlässt — nicht über einen Knopf. Unter der letzten Zeile steht
 * immer eine leere: Sie wird beim Verlassen angelegt, und die nächste leere ist
 * sofort bereit. Enter springt in die nächste Zeile, Esc verwirft die
 * Änderungen der Zeile.
 *
 * Das Raster richtet sich nach der **eigenen** Breite, nicht nach dem Fenster:
 * Der Block steht neben der Positionsliste, deren Breite das Fenster nicht
 * kennt. Passt die erste Zeile, stehen die Beschriftungen einmal im Kopf; passt
 * sie nicht, bricht der Block um und jedes Feld trägt seine Beschriftung.
 */

/**
 * Wie ein **bearbeitbares** Feld aussieht: heller Kasten mit Rand — so ist zu
 * sehen, wo etwas einzugeben ist. Gesperrt verliert es Rand und Fläche und liest
 * sich als Text (`speichern.md`). Ohne Innenabstand und Schriftgröße, damit auch
 * ein kleineres Feld denselben Rahmen tragen kann (AP-32 N-88).
 */
export const ZELLE_RAHMEN =
  'w-full min-w-0 rounded border border-border/70 bg-background focus:border-ring focus:outline-none disabled:border-transparent disabled:bg-transparent disabled:opacity-100 aria-[invalid=true]:border-destructive aria-[invalid=true]:bg-destructive/5'

export const zelle = `${ZELLE_RAHMEN} px-1.5 py-1 text-sm disabled:text-foreground`

// Seit AP-36 B in `lib/speichern/zahl.ts`; hier weiter angeboten für die bestehenden Aufrufer.
export { leseZahl, zahlText } from '@/lib/speichern/zahl'

/**
 * Preisformat: Tausenderpunkt und zwei Nachkommastellen, ohne Währung —
 * `1.234,50` (Auftraggeber 19.9.2026). Seit dem 20.9.2026 gilt es für **jedes**
 * Geldfeld von Symphenia und steht darum in `@symphenia/shared-types`
 * (`geld.ts`); hier weiter angeboten, damit die Masken ihre Importe behalten.
 */
export { geldText } from '@symphenia/shared-types'

const FELDER = 'input:not([type="checkbox"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'

/** Das Feld mit `data-start` zuerst (meist die Bezeichnung), sonst das erste freie. */
export function fokusErstesFeld(el: Element | null | undefined) {
  const start = el?.querySelector('[data-start]:not([disabled])') ?? el?.querySelector(FELDER)
  ;(start as HTMLElement | null)?.focus()
}

/** Breite des Containers — beobachtet, damit das Raster mitwächst. */
function useBreite() {
  const ref = useRef<HTMLDivElement>(null)
  const [breite, setBreite] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setBreite(el.clientWidth)
    const ro = new ResizeObserver(([e]) => setBreite(e.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return { ref, breite }
}

/**
 * Breite der Liste für die Maske, die ihre Spalten danach wählt (AP-35 Nachtrag 15, AP-37 Nachtrag 8): `ref` an
 * ein Element, das so breit ist wie die Liste. Solange nichts gemessen ist (auch in jsdom), ist die Breite 0 —
 * dann gilt die schmale, bisherige Anordnung.
 *
 * Beobachtet wird das Element, das beim ersten Zeichnen da ist. Es muss danach **dasselbe** bleiben — auch wenn die
 * Maske erst „Lädt …" und dann die Liste zeigt: beide Male dasselbe `div` an derselben Stelle. Ein Kontext außen
 * herum (`<X.Provider><div ref>`) ersetzt es beim Wechsel, und die Breite bliebe beim Wert des Ladens stehen
 * (AP-35 Nachtrag 17, in der Browserprobe gefunden).
 */
export function useListenBreite() {
  return useBreite()
}

export interface Spalte {
  key: string
  label: string
  /** Eigener Inhalt der Überschrift statt `label` (z. B. ein Knopf neben dem Titel) — nur im Kopf. */
  kopf?: ReactNode
  /** CSS-Spur im Raster, z.B. `6rem` oder `minmax(12rem,3fr)`. */
  spur: string
  /** Mindestbreite in Pixel — entscheidet, ob das Raster passt. */
  min: number
  /** Breite im Umbruch (Tailwind-Klasse). */
  schmal?: string
  rechts?: boolean
}

/**
 * Block rechts neben den Feldern, über die ganze Höhe der Zeile (Stückliste:
 * Status, Freigaben, Dateien). Reicht die Breite nicht, rückt er unter die Felder.
 */
export interface Seite {
  /** Tailwind-Breite, z. B. `w-[10.5rem]` — muss zu `min` passen. */
  breite: string
  min: number
  titel: ReactNode
  /**
   * Der Block steht **immer** unter den Feldern, auch wenn daneben Platz wäre (AP-35 Nachtrag 15, N15-14:
   * Stückliste unter 1.390 px — eine Zeile mit Status, Freigaben, Zeugnis und Dateien). `breite`, `min` und
   * `titel` bleiben dann ohne Wirkung.
   */
  nurDarunter?: boolean
}

interface RasterWert {
  raster: boolean
  spalten: Spalte[]
  /** Der Seitenblock steht neben den Feldern (sonst darunter). */
  seiteDaneben: boolean
  /** Deutlich abgesetzte Datensätze: mehr Rand, kräftigere Linie, Balken an der aktiven Zeile. */
  abgesetzt: boolean
  /** Tailwind-Breite des Seitenblocks. */
  seiteBreite: string
  /** Wie die Zeilen aufklappen (`components/klappmodus`); ohne Angabe wie bisher: beim Bearbeiten. */
  klappModus?: KlappModus
  /** Jede Zeile trägt die Spaltenüberschriften als kleine Zeile über ihren Feldern (`ZeilenListe.kopfJeZeile`). */
  kopfJeZeile: boolean
}

const RasterKontext = createContext<RasterWert>({ raster: false, spalten: [], seiteDaneben: false, abgesetzt: false, seiteBreite: '', kopfJeZeile: false })

/** Die Liste: misst sich, zeigt im Rastermodus den Kopf und reicht den Modus weiter. */
export function ZeilenListe({
  spalten,
  children,
  fuss,
  seite,
  abgesetzt = false,
  klappModus,
  kopfJeZeile = false,
}: {
  spalten: Spalte[]
  children: ReactNode
  fuss?: ReactNode
  seite?: Seite
  abgesetzt?: boolean
  /**
   * Die Spaltenüberschriften stehen zusätzlich als kleine Zeile über den Feldern **jeder** Zeile (Auftraggeber
   * 13.10.2026): In einer langen Liste ist der Kopf oben längst aus dem Bild, und welches Zahlenfeld Preiseinheit
   * und welches Rabatt ist, war dann nicht mehr zu sehen. Nur im Raster — im Umbruch trägt ohnehin jedes Feld
   * seine Beschriftung. Die erste Zeile lässt sie weg: Sie steht direkt unter dem Kopf.
   */
  kopfJeZeile?: boolean
  /**
   * Wie die Zeilen ihre weiteren Felder zeigen — immer, nie oder beim Bearbeiten (`useKlappModus`,
   * `KlappUmschalter`). Den Umschalter setzt die Maske in ihre Werkzeugleiste.
   */
  klappModus?: KlappModus
}) {
  const { ref, breite } = useBreite()
  const noetig = spalten.reduce((s, x) => s + x.min, 0) + spalten.length * 8 + 24
  const raster = breite >= noetig
  const seiteDaneben = Boolean(seite) && !seite?.nurDarunter && raster && breite >= noetig + (seite?.min ?? 0)
  const vorlage = spalten.map((s) => s.spur).join(' ')
  const kopf = (
    <div className="grid gap-x-2 px-3 py-1.5" style={{ gridTemplateColumns: vorlage }}>
      {spalten.map((s) => (
        <span key={s.key} className={s.rechts ? 'text-right' : ''}>
          {s.kopf ?? s.label}
        </span>
      ))}
    </div>
  )
  return (
    <RasterKontext.Provider value={{ raster, spalten, seiteDaneben, abgesetzt, seiteBreite: seite?.breite ?? '', klappModus, kopfJeZeile }}>
      <div ref={ref} className="rounded-lg border border-border bg-card">
        {raster && (
          <div className="border-b border-border bg-muted/50 text-xs text-muted-foreground">
            {seiteDaneben ? (
              <div className="flex">
                <div className="min-w-0 flex-1">{kopf}</div>
                <div className={`${seite!.breite} shrink-0 px-2.5 py-1.5`}>{seite!.titel}</div>
              </div>
            ) : (
              kopf
            )}
          </div>
        )}
        <div className={`${abgesetzt ? 'divide-y divide-foreground/20' : 'divide-y divide-border'} [&>[data-zeile]:first-child_[data-zeilenkopf]]:hidden`}>{children}</div>
        {fuss}
      </div>
    </RasterKontext.Provider>
  )
}

/** Raster-Spalten `von`–`bis` als `grid-column`; `undefined`, wenn eine fehlt. */
function spaltenBereich(spalten: Spalte[], von: string, bis = von): string | undefined {
  const a = spalten.findIndex((s) => s.key === von)
  const b = spalten.findIndex((s) => s.key === bis)
  return a < 0 || b < 0 ? undefined : `${a + 1} / ${b + 2}`
}

/**
 * Ein Feld der Zeilen 2 ff. (`zeilen` am Block), **spaltengenau** unter Zeile 1:
 * Es reicht von Spalte `von` bis `bis`. Im Umbruch bricht es frei um und nimmt
 * `schmal` als Breite. `label={null}` hält den Platz der Beschriftung frei, ohne
 * sie zu zeigen — dann muss das Feld selbst ein `aria-label` tragen.
 */
export function RasterFeld({
  von,
  bis,
  label,
  schmal = 'w-40',
  className = '',
  children,
}: {
  von: string
  bis?: string
  label: ReactNode | null
  schmal?: string
  className?: string
  children: ReactNode
}) {
  const { raster, spalten } = useContext(RasterKontext)
  const Huelle = label === null || typeof label === 'string' ? 'label' : 'div'
  return (
    <Huelle
      className={`block min-w-0 ${raster ? '' : schmal} ${className}`}
      style={raster ? { gridColumn: spaltenBereich(spalten, von, bis) } : undefined}
    >
      <span className="block text-[11px] text-muted-foreground" aria-hidden={label === null}>
        {label ?? '\u00a0'}
      </span>
      {children}
    </Huelle>
  )
}

/**
 * Eine Zelle der **Unterzeile** (`unterzeile` am Block), spaltengenau von `von` bis `bis` — ohne
 * Beschriftungszeile: Die Felder darin erklären sich selbst (`aria-label`, sprechende leere Auswahl).
 * Nur fürs Raster gedacht; im Umbruch nimmt die Unterzeile stattdessen `RasterFeld` mit Beschriftung
 * (siehe `useRaster`).
 */
export function RasterZelle({ von, bis, className = '', children }: { von: string; bis?: string; className?: string; children: ReactNode }) {
  const { raster, spalten } = useContext(RasterKontext)
  return (
    <div className={`min-w-0 ${className}`} style={raster ? { gridColumn: spaltenBereich(spalten, von, bis) } : undefined}>
      {children}
    </div>
  )
}

/** Steht die Liste im Raster (sonst im Umbruch, jedes Feld mit eigener Beschriftung)? */
export function useRaster() {
  return useContext(RasterKontext).raster
}

/** Kurzinfo unter Zeile 1 einer zugeklappten Zeile, spaltengenau (z. B. Alternativmenge, Werkstoff). */
export function RasterInfo({ von, bis, rechts, children }: { von: string; bis?: string; rechts?: boolean; children: ReactNode }) {
  const { raster, spalten } = useContext(RasterKontext)
  return (
    <span
      className={`truncate px-1.5 ${rechts && raster ? 'text-right' : ''}`}
      style={raster ? { gridColumn: spaltenBereich(spalten, von, bis) } : undefined}
    >
      {children}
    </span>
  )
}

/**
 * Ein Feld der ersten Zeile. Im Rastermodus sitzt es in seiner Spalte, im
 * Umbruch trägt es seine Beschriftung.
 */
export function Feld({ spalte, children, label, leer }: { spalte: string; children: ReactNode; label?: string; leer?: boolean }) {
  const { raster, spalten } = useContext(RasterKontext)
  const s = spalten.find((x) => x.key === spalte)
  if (raster) return <div className={`min-w-0 ${s?.rechts ? 'text-right' : ''}`}>{children}</div>
  // Im Umbruch trägt ein leeres Feld keine Beschriftung — sie stünde über nichts.
  if (leer) return null
  const beschriftung = label ?? s?.label
  return (
    <label className={`block min-w-0 ${s?.schmal ?? 'w-28'}`}>
      {beschriftung && <span className="block text-[11px] text-muted-foreground">{beschriftung}</span>}
      {children}
    </label>
  )
}

/** Ein Feld der zweiten Zeile — immer mit Beschriftung, bricht frei um. */
export function ZusatzFeld({ label, breite = 'w-48', children }: { label: string; breite?: string; children: ReactNode }) {
  return (
    <label className={`block min-w-0 ${breite}`}>
      <span className="block text-[11px] text-muted-foreground">{label}</span>
      {children}
    </label>
  )
}

interface BlockProps {
  /**
   * Der Fokus hat den Block verlassen. `perTastatur`: über Enter oder Tab —
   * dann darf die neue leere Zeile den Fokus bekommen. Mit `gruppe` speichert
   * der Block die Felder selbst; `onVerlassen` bleibt für das Anlegen der
   * leeren letzten Zeile.
   */
  onVerlassen?: (perTastatur: boolean) => void
  /**
   * AP-36: Die Zeile ist eine **Gruppe** des Speicherbausteins (`useGruppe`).
   * Ihre Felder (`useFeld`) speichern gemeinsam beim Verlassen der Zeile; Zustand
   * und Fehler zeigt der Block.
   */
  gruppe?: Gruppe
  /** Esc: Änderungen der Zeile verwerfen. */
  onVerwerfen?: () => void
  /** Zweite Zeile — erscheint, solange die Zeile bearbeitet wird oder `zusatzImmer`. */
  zusatz?: ReactNode
  zusatzImmer?: boolean
  /**
   * Die Zeile lässt sich **ohne Fokus** auf- und zuklappen: ein Pfeil unten rechts zeigt `zeilen` und `zusatz`.
   * Für gesperrte Zeilen — ihre Felder nehmen keinen Fokus, und ohne ihn klappt nichts auf. Was beim Bearbeiten
   * in den weiteren Zeilen steht, war damit nach dem Sperren gar nicht mehr zu sehen (Eingangsrechnung nach der
   * Freigabe, Auftraggeber 10.10.2026). Eine bearbeitbare Zeile braucht das nicht: Sie klappt mit dem Fokus auf.
   */
  aufklappbar?: boolean
  /**
   * Weitere Zeilen **im Raster** unter Zeile 1 — Felder als `RasterFeld`. Sie
   * erscheinen wie `zusatz`, solange die Zeile bearbeitet wird oder `zusatzImmer`.
   */
  zeilen?: ReactNode[]
  /**
   * Eine zweite Zeile direkt unter Zeile 1, die **immer** zu sehen ist — im Raster spaltengenau
   * (`RasterZelle`), im Umbruch wie jede weitere Zeile (`RasterFeld`). Für Felder, die zur Zeile gehören,
   * aber Zeile 1 zu eng machen würden (Eingangsrechnung: Steuer, Kostenziel, Sachkonto unter der Bezeichnung).
   */
  unterzeile?: ReactNode
  /**
   * Kurzinfo unter Zeile 1, nur solange die Zeile **nicht** bearbeitet wird — Felder als `RasterInfo`. Knöpfe
   * und Verweise darin brauchen kein eigenes `onMouseDown` (`klickOhneFokus`): Mit dem Fokus verschwände die
   * Kurzinfo samt dem Knopf, auf den gerade gedrückt wird.
   */
  zuInfo?: ReactNode
  /**
   * Block rechts über die ganze Höhe (siehe `ZeilenListe.seite`); bekommt, ob die Zeile bearbeitet wird. Ein
   * Klick auf ein Bedienelement darin macht die Zeile aktiv — aufgeklappt wird erst nach dem Loslassen
   * (`aufklappenNachLoslassen`).
   */
  seite?: (aktiv: boolean) => ReactNode
  /**
   * Eigene Tab-Reihenfolge: Felder mit `data-haupt="n"` bilden die Hauptkette,
   * Tab springt nur zwischen ihnen und am Ende in die nächste Zeile. Aus einem
   * anderen Feld geht Tab zum nächsten Feld in Lesereihenfolge; `data-weiter="n"`
   * springt stattdessen zum Hauptfeld `n`. In der letzten Zeile der Liste verlässt
   * Tab am Ende die Zeile: Die leere Zeile (`neu`) mit einer Eingabe wird angelegt
   * wie mit Enter, sonst geht es hinter der Liste weiter.
   */
  tabKette?: boolean
  /**
   * Aufgeklapptes Detail unter dem Block (Dateien, Texte …). Es arbeitet für sich: Der Fokus darin klappt eine
   * zugeklappte Zeile nicht auf, und Knöpfe und Häkchen brauchen kein eigenes `onMouseDown` (`klickOhneFokus`).
   */
  detail?: ReactNode
  fehler?: string | null
  /**
   * Kleine Zeile unter den Zusatzzeilen. Knöpfe und Häkchen darin brauchen kein eigenes `onMouseDown`: Der
   * Block hält den Fokus beim Drücken selbst zurück (`klickOhneFokus`).
   */
  hinweis?: ReactNode | ((aktiv: boolean) => ReactNode)
  /**
   * Beim ersten Erscheinen das erste Feld fokussieren (neue leere Zeile). Wer es an der leeren Zeile setzt — auch
   * `false` —, sagt damit: Nach dem Anlegen per Tastatur bekommt die nächste leere Zeile den Fokus. Dann verlässt
   * Tab im letzten Feld der leeren Zeile sie wie Enter, statt aus der Liste hinauszugehen.
   */
  autoFokus?: boolean
  gedimmt?: boolean
  neu?: boolean
  eingerueckt?: boolean
  /** Kennung für das Ziehen (`data-key`, `components/ziehen`). */
  schluessel?: string
  /** Die Zeile wird gerade gezogen — abgeblendet, sie bleibt stehen (N-67). */
  gezogen?: boolean
  /** Ziellinie beim Ziehen: vor dieser Zeile (`oben`) oder am Ende (`unten`). */
  ziellinie?: 'oben' | 'unten' | null
  /**
   * Die Zeile lässt sich an ihrer freien Fläche greifen und verschieben
   * (N-64 neu) — nur innerhalb eines `ZiehBereich` und mit `schluessel`.
   */
  ziehbar?: boolean
  /** Die Zeile ist markiert — der Rand ist gefüllt, die Zeile getönt. */
  markiert?: boolean
  /** Druck auf den linken Rand (`useRandMarkieren().amRand`); ohne sie kein Rand. */
  onRand?: (e: ReactMouseEvent) => void
  /** Beschriftung des Rands für Vorleser, z. B. die Bezeichnung der Zeile. */
  randLabel?: string
  /**
   * Dateien aus dem Explorer auf die Zeile ziehen (AP-35 Nachtrag 6). Ohne
   * diese Prop nimmt die Zeile keine Dateien an.
   */
  onDateien?: (dateien: File[]) => void
  children: ReactNode
}

/** Zieht der Benutzer Dateien (nicht Text, nicht eine Zeile)? */
const sindDateien = (e: DragEvent) => [...e.dataTransfer.types].includes('Files')

const BEDIENBAR = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
/** Was einen **Klick** braucht: Knopf, Verweis, Häkchen samt Beschriftung. */
const KLICKBAR = 'button, a, label, input[type="checkbox"], input[type="radio"]'
/** Was den **Fokus** braucht, um bedient zu werden: Textfeld und Auswahl — auch in einer Beschriftung. */
const BRAUCHT_FOKUS = 'input:not([type="checkbox"]):not([type="radio"]), select, textarea, [contenteditable="true"]'

/**
 * Unter den aufklappenden Zeilen (Hinweis, Fehler, „Erneut", Detail) nimmt der Druck auf ein klickbares Element
 * den Fokus nicht vorweg — erst der Klick wirkt, wie am „Zuklappen". Mit dem Fokus klappte eine zugeklappte
 * Zeile ihre Zusatzzeilen auf; kam der Druck dagegen aus der aufgeklappten Zeile auf eine Beschriftung, nahm er
 * ihr den Fokus, und sie klappte zu. Beide Male verschob sich, was **darüber** steht: Das Element lag beim
 * Loslassen nicht mehr unter dem Zeiger, und der Klick kam nie an (Eingangsrechnung 7.10.2026: „Einkaufspreis
 * übernehmen", „Bestätigen", „Bezug lösen"; Kalkulation 8.10.2026: „Hochladen" und „entfernen" in den Dateien
 * einer Kostenzeile). Textfeld und Auswahl brauchen den Fokus selbst und bekommen ihn.
 *
 * Dasselbe in der Kurzinfo (`zuInfo`), die **über** den Zusatzzeilen steht: Sie verschwindet, sobald die Zeile
 * bearbeitet wird — samt dem Knopf, auf den gerade gedrückt wird (Kalkulation: Büroklammer, Marke der Anfrage).
 * Es kam kein Klick, und die Zeile blieb aufgeklappt stehen, ohne dass der Fokus noch in ihr war.
 */
function klickOhneFokus(e: ReactMouseEvent) {
  const ziel = e.target as HTMLElement
  if (ziel.closest(KLICKBAR) && !ziel.closest(BRAUCHT_FOKUS)) e.preventDefault()
}

const nummer = (el: Element) => Number(el.getAttribute('data-haupt'))
const sichtbar = (el: HTMLElement) => el.tabIndex >= 0 && el.getClientRects().length > 0

/** Hauptfelder einer Zeile, nach `data-haupt` sortiert. */
function hauptfelder(zeile: Element): HTMLElement[] {
  return [...zeile.querySelectorAll<HTMLElement>('[data-haupt]')]
    .filter((x) => !(x as HTMLInputElement).disabled)
    .sort((a, b) => nummer(a) - nummer(b))
}

/**
 * Ziel eines Tab-Sprungs innerhalb der Zeile — oder `null`, wenn die Kette
 * hier endet (dann geht es in die Nachbarzeile).
 */
function tabZiel(zeile: HTMLElement, ziel: HTMLElement, rueckwaerts: boolean): HTMLElement | null {
  if (ziel.hasAttribute('data-haupt')) {
    const kette = hauptfelder(zeile)
    const n = nummer(ziel)
    return (rueckwaerts ? kette.filter((x) => nummer(x) < n).pop() : kette.find((x) => nummer(x) > n)) ?? null
  }
  if (!rueckwaerts && ziel.hasAttribute('data-weiter')) {
    const n = Number(ziel.getAttribute('data-weiter'))
    return hauptfelder(zeile).find((x) => nummer(x) === n) ?? null
  }
  const alle = [...zeile.querySelectorAll<HTMLElement>(BEDIENBAR)].filter(sichtbar)
  const i = alle.indexOf(ziel)
  return (rueckwaerts ? alle[i - 1] : alle[i + 1]) ?? null
}

/**
 * Das Hauptfeld, in dem die Kette in der Nachbarzeile weitergeht. Eine Zeile ohne freies Hauptfeld (storniert,
 * gesperrt) zählt nicht: An ihr endete die Kette sonst, und Tab liefe in der Zeile davor im Kreis.
 */
function nachbarFeld(zeile: Element, rueckwaerts: boolean): HTMLElement | null {
  const weiter = (el: Element) => (rueckwaerts ? el.previousElementSibling : el.nextElementSibling)
  for (let nachbar = weiter(zeile); nachbar?.hasAttribute('data-zeile'); nachbar = weiter(nachbar)) {
    const kette = hauptfelder(nachbar)
    if (kette.length) return rueckwaerts ? kette[kette.length - 1] : kette[0]
  }
  return null
}

/** Steht `ziel` zuletzt in der Tab-Reihenfolge der Zeile, und kommt nach ihr keine Zeile mehr? */
function istLetztesFeld(zeile: HTMLElement, ziel: HTMLElement): boolean {
  if (zeile.nextElementSibling?.hasAttribute('data-zeile')) return false
  const alle = [...zeile.querySelectorAll<HTMLElement>(BEDIENBAR)].filter(sichtbar)
  return alle.length > 0 && alle[alle.length - 1] === ziel
}

export function ZeilenBlock({
  onVerlassen,
  gruppe,
  onVerwerfen,
  zusatz,
  zusatzImmer,
  aufklappbar,
  zeilen,
  unterzeile,
  zuInfo,
  seite,
  tabKette,
  detail,
  fehler,
  hinweis,
  autoFokus,
  gedimmt,
  neu,
  eingerueckt,
  schluessel,
  gezogen,
  ziellinie,
  ziehbar,
  markiert,
  onRand,
  randLabel,
  onDateien,
  children,
}: BlockProps) {
  const { raster, spalten, seiteDaneben, abgesetzt, seiteBreite, klappModus, kopfJeZeile } = useContext(RasterKontext)
  const ref = useRef<HTMLDivElement | null>(null)
  const greifen = useZeileGreifen(schluessel, !ziehbar)
  const [aktiv, setAktiv] = useState(false)
  /** Von Hand aufgeklappt (`aufklappbar`) — unabhängig vom Fokus. */
  const [aufgeklappt, setAufgeklappt] = useState(false)
  /** Zähler statt Schalter: `dragleave` feuert auch beim Wechsel in ein Kind-Element. */
  const [dateiUeber, setDateiUeber] = useState(0)
  const perTastatur = useRef(false)
  const verworfen = useRef(false)
  /** In der Zeile wurde etwas eingegeben — die leere letzte Zeile hat dann beim Verlassen etwas anzulegen. */
  const beruehrt = useRef(false)
  /** Ausgang der Tab-Kette am Ende der Zeile — siehe dort, wo er gezeichnet wird. */
  const ausgang = useRef<HTMLSpanElement>(null)
  const detailRef = useRef<HTMLDivElement>(null)
  /** Ein Druck im Seitenblock ist noch nicht losgelassen: Die Zeile klappt so lange nicht auf. */
  const wartetAufLoslassen = useRef(false)
  const lauscherAb = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (autoFokus) fokusErstesFeld(ref.current)
  }, [autoFokus])
  useEffect(() => () => lauscherAb.current?.(), [])

  /**
   * Ein Druck auf ein Bedienelement im Seitenblock einer zugeklappten Zeile gibt ihr den Fokus wie immer — die
   * Maske baut darauf: Freigaben und Dateien erscheinen erst an der aktiven Zeile, und ein Fenster des Blocks
   * (`Aufklapper`) gibt den Fokus beim Schließen an seinen Öffner zurück. **Aufgeklappt** wird aber erst nach
   * dem Loslassen. Sonst verschiebt sich der Block zwischen Drücken und Loslassen, und der Klick kommt nie an
   * (Stückliste 8.10.2026, im Browser gemessen): Steht er unter den Feldern, rutscht er um die Zusatzzeilen
   * nach unten (187 px) — das ist bis 908 px Listenbreite so, also schon im 1440er Fenster. Steht er daneben,
   * rückt er zur Seite, sobald die Seite durch das Aufklappen ihren Rollbalken bekommt (15 px — unter dem Zeiger
   * lag der Nachbarknopf).
   */
  function aufklappenNachLoslassen() {
    if (wartetAufLoslassen.current) return
    wartetAufLoslassen.current = true
    const losgelassen = () => {
      lauscherAb.current?.()
      // Der Klick folgt dem Loslassen im selben Zug — erst danach darf sich etwas verschieben.
      setTimeout(() => {
        wartetAufLoslassen.current = false
        if (ref.current?.contains(document.activeElement)) setAktiv(true)
      }, 0)
    }
    // `dragend`: Wird aus dem Druck ein Ziehen (Verweis, Bild), kommt kein `mouseup`.
    window.addEventListener('mouseup', losgelassen, true)
    window.addEventListener('dragend', losgelassen, true)
    lauscherAb.current = () => {
      window.removeEventListener('mouseup', losgelassen, true)
      window.removeEventListener('dragend', losgelassen, true)
      lauscherAb.current = null
    }
  }

  const vorlage = spalten.map((s) => s.spur).join(' ')
  // Die Liste kann vorgeben, dass alle Zeilen offen oder alle zu bleiben (`klappModus`) — dann gibt es an der
  // Zeile nichts auf- oder zuzuklappen. Sonst wie bisher: offen, solange sie bearbeitet wird.
  const festerModus = klappModus === 'auf' || klappModus === 'zu'
  const offen = zeileOffen(klappModus, Boolean(aktiv || zusatzImmer || (aufklappbar && aufgeklappt)))
  /** Der Pfeil der gesperrten Zeile: nur, wo es etwas aufzuklappen gibt und nicht ohnehin alles offen steht. */
  const mitPfeil = Boolean(aufklappbar) && !festerModus && !aktiv && !zusatzImmer && Boolean(zeilen?.length || zusatz)
  /** Nur wo das Bearbeiten etwas aufklappt, gibt es etwas zuzuklappen. */
  const kannZuklappen = aktiv && !festerModus && !zusatzImmer && Boolean(zeilen?.length || zusatz)
  const rasterZeile = (inhalt: ReactNode, key: number) => (
    <div
      key={key}
      className={raster ? 'mt-1.5 grid items-start gap-x-2' : 'mt-1.5 flex flex-wrap items-end gap-x-2 gap-y-1'}
      style={raster ? { gridTemplateColumns: vorlage } : undefined}
    >
      {inhalt}
    </div>
  )
  const felder = (
    <>
      {raster && kopfJeZeile && (
        // Dieselben Spuren wie die Felder darunter, eingerückt wie der Text in einem Feld (Rahmen + `px-1.5`).
        <div data-zeilenkopf aria-hidden className="grid gap-x-2 text-[10px] leading-[14px] text-muted-foreground" style={{ gridTemplateColumns: vorlage }}>
          {spalten.map((s) => (
            <span key={s.key} className={`truncate px-[7px] ${s.rechts ? 'text-right' : ''}`}>
              {s.label}
            </span>
          ))}
        </div>
      )}
      <div
        className={raster ? 'grid items-center gap-x-2' : 'flex flex-wrap items-end gap-x-2 gap-y-1'}
        style={raster ? { gridTemplateColumns: vorlage } : undefined}
      >
        {children}
      </div>
      {unterzeile && (
        <div
          className={raster ? 'mt-1 grid items-center gap-x-2' : 'mt-1.5 flex flex-wrap items-end gap-x-2 gap-y-1'}
          style={raster ? { gridTemplateColumns: vorlage } : undefined}
        >
          {unterzeile}
        </div>
      )}
      {zuInfo && !offen && (
        <div
          className={raster ? 'grid gap-x-2 text-[11px] text-muted-foreground' : 'flex flex-wrap gap-x-3 text-[11px] text-muted-foreground'}
          style={raster ? { gridTemplateColumns: vorlage } : undefined}
          onMouseDown={klickOhneFokus}
        >
          {zuInfo}
        </div>
      )}
      {zeilen && offen && zeilen.map(rasterZeile)}
      {zusatz && offen && <div className="mt-1.5 flex flex-wrap items-end gap-x-2 gap-y-1">{zusatz}</div>}
      {(hinweis || fehler || gruppe) && (
        <div onMouseDown={klickOhneFokus}>
          {hinweis && <div className="mt-1 text-xs text-muted-foreground">{typeof hinweis === 'function' ? hinweis(aktiv) : hinweis}</div>}
          {fehler && <p className="mt-1 text-xs text-destructive">{fehler}</p>}
          {gruppe && !fehler && <FeldStatus status={gruppe} />}
        </div>
      )}
      {mitPfeil && (
        // Wie „Zuklappen" unten: Der Druck nimmt nichts vorweg, erst der Klick wirkt.
        <button
          type="button"
          data-nicht-greifen
          aria-expanded={aufgeklappt}
          title={aufgeklappt ? 'Zeile zuklappen' : 'Zeile aufklappen — zeigt die weiteren Felder'}
          aria-label={aufgeklappt ? 'Zeile zuklappen' : 'Zeile aufklappen'}
          className="absolute bottom-0 right-0 z-[1] rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setAufgeklappt((a) => !a)}
        >
          {aufgeklappt ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      )}
      {kannZuklappen && (
        // Zuklappen (Auftraggeber 30.9.2026) heißt die Zeile verlassen: Sie speichert wie beim Wegklicken.
        // Der Druck nimmt dem Feld den Fokus nicht vorweg, erst der Klick — so bleibt es ein Vorgang.
        <button
          type="button"
          tabIndex={-1}
          data-nicht-greifen
          title="Zeile zuklappen"
          aria-label="Zeile zuklappen"
          className="absolute bottom-0 right-0 z-[1] rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const fokus = document.activeElement as HTMLElement | null
            if (fokus && ref.current?.contains(fokus)) fokus.blur()
          }}
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      )}
    </>
  )
  const block = seite?.(aktiv)
  // Die Ziellinie ist auch ein Innenschatten — sie gewinnt gegen den Balken der aktiven Zeile.
  const schatten =
    ziellinie === 'oben'
      ? 'shadow-[inset_0_2px_0_hsl(var(--primary))]'
      : ziellinie === 'unten'
        ? 'shadow-[inset_0_-2px_0_hsl(var(--primary))]'
        : aktiv && abgesetzt
          ? 'shadow-[inset_3px_0_0_hsl(var(--primary))]'
          : ''
  const dateiZiel = dateiUeber > 0

  return (
    <div
      ref={(el) => {
        ref.current = el
        greifen.ref(el)
      }}
      {...greifen.listeners}
      data-zeile
      data-key={schluessel}
      // Die bearbeitete Zeile trägt überall die Tönung der Stückliste (Auftraggeber 30.9.2026) — vorher war sie
      // nur in den abgesetzten Listen `bg-primary/10`, sonst schwächer.
      className={`relative ${abgesetzt ? 'py-2.5' : 'px-3 py-1.5'} ${greifen.listeners ? 'zeile-greifbar' : ''} ${markiert ? 'bg-primary/15' : aktiv ? 'bg-primary/10' : ''} ${schatten} ${gezogen ? 'opacity-40' : gedimmt ? 'opacity-60' : ''} ${neu && !aktiv ? 'bg-muted/20' : ''} ${eingerueckt ? 'pl-8' : ''} ${dateiZiel ? 'bg-primary/10 outline-dashed outline-2 -outline-offset-2 outline-primary' : ''}`}
      onDragEnter={
        onDateien
          ? (e) => {
              if (!sindDateien(e)) return
              e.preventDefault()
              setDateiUeber((n) => n + 1)
            }
          : undefined
      }
      onDragOver={
        onDateien
          ? (e) => {
              if (!sindDateien(e)) return
              e.preventDefault()
              e.dataTransfer.dropEffect = 'copy'
            }
          : undefined
      }
      onDragLeave={onDateien ? (e) => sindDateien(e) && setDateiUeber((n) => Math.max(0, n - 1)) : undefined}
      onDrop={
        onDateien
          ? (e) => {
              if (!sindDateien(e)) return
              e.preventDefault()
              setDateiUeber(0)
              const dateien = [...e.dataTransfer.files]
              if (dateien.length) onDateien(dateien)
            }
          : undefined
      }
      onFocus={(e) => {
        gruppe?.onFocus()
        // Das Detail steht unter den aufklappenden Zeilen und arbeitet für sich (Dateien einer Kostenzeile):
        // Der Fokus darin klappt eine zugeklappte Zeile nicht auf — eine Auswahl dort rutschte sonst unter dem
        // Zeiger weg (Kalkulation 8.10.2026: „Dateiart" 154 px). Eine aufgeklappte Zeile bleibt, wie sie ist.
        if (!aktiv && detailRef.current?.contains(e.target)) return
        if (wartetAufLoslassen.current) return
        setAktiv(true)
      }}
      onChange={() => {
        beruehrt.current = true
      }}
      onBlur={(e) => {
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
        setAktiv(false)
        const tastatur = perTastatur.current
        perTastatur.current = false
        // Esc hat die Zeile eben zurückgesetzt — der Entwurf in dieser Closure ist noch der alte.
        if (verworfen.current) {
          verworfen.current = false
          return
        }
        if (gruppe) void gruppe.verlassen()
        onVerlassen?.(tastatur)
      }}
      onKeyDown={(e) => {
        const ziel = e.target as HTMLElement
        if (e.key === 'Tab') perTastatur.current = true
        if (e.key === 'Tab' && tabKette && ref.current && ref.current.contains(ziel) && ziel !== ausgang.current) {
          const naechstes = tabZiel(ref.current, ziel, e.shiftKey)
          if (naechstes) {
            e.preventDefault()
            naechstes.focus()
            return
          }
          // Ende der Kette: erstes (bzw. letztes) Hauptfeld der Nachbarzeile.
          const dort = nachbarFeld(ref.current, e.shiftKey)
          if (dort) {
            e.preventDefault()
            dort.focus()
            return
          }
          // Keine Zeile mehr dahinter — Tab verlässt die Zeile. Dem Browser überlassen, ginge es in das Feld, das
          // im Dokument folgt, und das steht oft noch in **dieser** Zeile: in der Stückliste nach dem Rabatt die
          // Alternativmenge, von dort die Kette entlang wieder zum Rabatt. Die leere letzte Zeile wurde so mit
          // Tab nie verlassen und nie angelegt (Auftraggeber 9.10.2026).
          if (!e.shiftKey) {
            if (neu && beruehrt.current) {
              // Wie Enter: Das Feld gibt den Fokus ab, die Zeile wird angelegt, und die nächste leere bekommt
              // ihn (`perTastatur`). Nicht weiter zum Bedienelement hinter der Liste — bis die neue Zeile
              // dasteht, landete dort, was schon für sie getippt wird.
              e.preventDefault()
              ziel.blur()
            } else {
              // Nichts anzulegen: Tab geht weiter wie in jedem Formular. Vom Ausgang am Ende der Zeile findet
              // der Browser den Weg selbst — das nächste Bedienelement der Seite oder hinaus aus ihr.
              ausgang.current?.focus({ preventScroll: true })
            }
          }
          return
        }
        // Ohne eigene Kette gilt die Reihenfolge des Browsers — bis auf das letzte Feld der leeren Zeile mit
        // Eingabe, wo die Liste per Tastatur weitergeht (`autoFokus`). Hinter ihr kommt in der Liste nichts mehr:
        // Der Browser ginge zum nächsten Bedienelement der Seite oder aus ihr hinaus, bevor die Zeile angelegt ist
        // — und aus der Adresszeile holt die nächste leere Zeile den Fokus nicht zurück (Verbrauchsmaterial,
        // Auftraggeber 9.10.2026). Also auch hier wie Enter.
        if (e.key === 'Tab' && !e.shiftKey && !tabKette && neu && autoFokus !== undefined && beruehrt.current && ref.current && istLetztesFeld(ref.current, ziel)) {
          e.preventDefault()
          ziel.blur()
          return
        }
        if (e.key === 'Escape' && (onVerwerfen || gruppe)) {
          e.preventDefault()
          gruppe?.verwerfen()
          onVerwerfen?.()
          verworfen.current = true
          beruehrt.current = false
          ziel.blur()
        }
        if (e.key === 'Enter' && ziel.tagName !== 'TEXTAREA' && ziel.tagName !== 'BUTTON' && !e.nativeEvent.isComposing) {
          e.preventDefault()
          perTastatur.current = true
          const naechste = ref.current?.nextElementSibling
          if (naechste?.hasAttribute('data-zeile')) fokusErstesFeld(naechste)
          else ziel.blur()
        }
      }}
    >
      {onRand && (
        // Der linke Rand markiert (N-64 neu). Er liegt im Innenabstand der Zeile
        // (px-3) und deckt so kein Feld zu.
        <div
          data-rand
          role="checkbox"
          aria-checked={Boolean(markiert)}
          aria-label={randLabel ? `${randLabel} markieren` : 'Zeile markieren'}
          title="Zeile markieren — Strg: dazu, Umschalt: Bereich"
          className={`absolute inset-y-0 left-0 z-[1] w-2 ${markiert ? 'bg-primary' : 'hover:bg-primary/30'}`}
          style={{ cursor: RAND_ZEIGER }}
          onMouseDown={onRand}
        />
      )}
      <GruppenBereich gruppe={gruppe}>
      {seite ? (
        <div className={seiteDaneben ? 'flex' : ''}>
          <div className={`relative min-w-0 flex-1 ${abgesetzt ? 'px-3' : ''}`}>{felder}</div>
          <div
            className={
              seiteDaneben
                ? `relative ${seiteBreite} shrink-0 border-l border-border/80 px-2.5 text-xs`
                : 'relative mx-3 mt-2 flex flex-wrap items-start gap-x-5 gap-y-1 border-t border-dashed border-border pt-2 text-xs'
            }
            // Ein Klick auf Text im Block (etwa die Beschriftung eines Häkchens)
            // nähme sonst zuerst den Fokus aus der Zeile: Sie klappte zu, bevor
            // der Klick ankommt, und er ginge ins Leere.
            onMouseDown={(e) => {
              if (!(e.target as HTMLElement).closest('input, select, textarea, button, a')) e.preventDefault()
              else if (!aktiv && e.button === 0) aufklappenNachLoslassen()
            }}
          >
            {block}
          </div>
        </div>
      ) : (
        <div className={`relative ${abgesetzt ? 'px-3' : ''}`}>{felder}</div>
      )}
      </GruppenBereich>
      {detail && (
        <div ref={detailRef} onMouseDown={klickOhneFokus}>
          {detail}
        </div>
      )}
      {dateiZiel && (
        <span className="pointer-events-none absolute right-3 top-1 z-10 rounded bg-primary px-2 py-0.5 text-xs text-primary-foreground shadow">Dateien an diese Zeile hängen</span>
      )}
      {tabKette && (
        // Ausgang der Tab-Kette: das letzte Element der Zeile, selbst nicht in der Tab-Reihenfolge. Endet die
        // Kette in der letzten Zeile der Liste, bekommt er den Fokus — von hier geht Tab hinter die Zeile, nicht
        // in ein Feld, das in ihr noch folgt, und auch nicht erst durch Tag, Monat und Jahr eines Datumsfelds.
        <span ref={ausgang} tabIndex={-1} data-ausgang className="block h-0 outline-none" />
      )}
    </div>
  )
}

/** Der Seitenblock steht neben den Feldern (sonst darunter) — für Aufklapper, die sich danach ausrichten. */
export function useSeiteDaneben() {
  return useContext(RasterKontext).seiteDaneben
}

/**
 * Vorschlagsliste unter einem Feld. `onMouseDown` verhindert, dass der Klick
 * den Fokus aus dem Block nimmt — sonst würde die Zeile gespeichert, bevor der
 * Vorschlag übernommen ist. Das gilt für die ganze Liste, nicht nur für ihre
 * Einträge: Ein Druck auf den Rollbalken nahm dem Feld sonst den Fokus, und die
 * Liste ging zu, bevor sie sich rollen ließ (Auftraggeber 13.10.2026).
 *
 * **Mit der Tastatur** (Auftraggeber 13.10.2026): ↓ ↑ gehen durch die Liste — ↑ über
 * den ersten Eintrag hinaus hebt die Wahl wieder auf. Tab und Enter übernehmen den
 * Eintrag, auf dem man steht, und gehen dann weiter wie immer (nächstes Feld bzw.
 * nächste Zeile). Mit `allein` gilt ein **einziger** Treffer als gewählt, ohne dass
 * man erst mit ↓ auf ihn gehen muss.
 *
 * Die Tasten kommen am Feld an, nicht an der Liste. Sie hört darum am gemeinsamen
 * Elternelement mit — dem `relative`-Rahmen um Feld und Liste —, und zwar vor React:
 * So ist der Vorschlag schon übernommen, wenn der Zeilenblock auf dieselbe Taste hin
 * die Zeile verlässt und speichert oder anlegt.
 */
export function Vorschlaege<T>({
  eintraege,
  schluessel,
  onWahl,
  darstellung,
  allein = false,
  weiter = true,
  fest = false,
}: {
  eintraege: T[]
  schluessel: (t: T) => string
  /** `perTastatur`: mit Tab oder Enter übernommen, nicht mit der Maus. */
  onWahl: (t: T, perTastatur: boolean) => void
  darstellung: (t: T) => ReactNode
  /** Bleibt genau ein Treffer, übernehmen ihn Tab und Enter auch ohne ↓ (Artikel, Lieferant). */
  allein?: boolean
  /**
   * Nach dem Übernehmen wirkt die Taste weiter: Tab geht ins nächste Feld, Enter in die nächste Zeile.
   * `false`, wo die Wahl selbst schon alles tut (eine Vorlage legt die Zeile an) — die Taste endet dann hier.
   */
  weiter?: boolean
  /**
   * Die Liste steht fest im Fenster unter (bei wenig Platz über) ihrem Feld, statt im Rahmen des Felds — für
   * Felder in einer Tabelle: Deren Rollbereich schnitte eine Liste an den letzten Zeilen ab.
   */
  fest?: boolean
}) {
  const liste = useRef<HTMLDivElement>(null)
  const [ort, setOrt] = useState<CSSProperties | null>(null)
  /** Von Hand gewählt (↓ ↑); `null`: noch nicht — dann gilt der einzige Treffer oder keiner. */
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)
  // Eine andere Trefferliste: Die Wahl von Hand galt der alten.
  const kennung = eintraege.map(schluessel).join('\n')
  const [fuer, setFuer] = useState(kennung)
  if (fuer !== kennung) {
    setFuer(kennung)
    setGewaehlt(null)
  }
  const index = fuer !== kennung || gewaehlt === null ? (allein && eintraege.length === 1 ? 0 : -1) : Math.min(gewaehlt, eintraege.length - 1)
  const aktuell = useRef({ eintraege, index, onWahl, weiter })
  aktuell.current = { eintraege, index, onWahl, weiter }
  const offen = eintraege.length > 0

  useEffect(() => {
    const rahmen = liste.current?.parentElement
    if (!offen || !rahmen) return
    const taste = (e: KeyboardEvent) => {
      const ziel = e.target as HTMLElement | null
      if (!ziel || liste.current?.contains(ziel) || !['INPUT', 'TEXTAREA'].includes(ziel.tagName)) return
      if (e.altKey || e.ctrlKey || e.metaKey || e.isComposing) return
      const a = aktuell.current
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // Im Feld spränge der Cursor sonst an den Anfang bzw. das Ende.
        e.preventDefault()
        setGewaehlt(e.key === 'ArrowDown' ? Math.min(a.index + 1, a.eintraege.length - 1) : Math.max(a.index - 1, -1))
        return
      }
      if ((e.key !== 'Enter' && e.key !== 'Tab') || a.index < 0) return
      // Enter schickt sonst ein umgebendes Formular ab; Tab soll weitergehen — außer die Wahl tut schon alles.
      if (e.key === 'Enter' || !a.weiter) e.preventDefault()
      if (!a.weiter) e.stopPropagation()
      // Sofort ausführen: Die Taste läuft gleich weiter zum Zeilenblock, und der soll die Zeile schon mit dem
      // übernommenen Vorschlag vorfinden.
      flushSync(() => a.onWahl(a.eintraege[a.index], true))
    }
    rahmen.addEventListener('keydown', taste)
    return () => rahmen.removeEventListener('keydown', taste)
  }, [offen])

  useEffect(() => {
    if (index >= 0) liste.current?.children[index]?.scrollIntoView?.({ block: 'nearest' })
  }, [index, kennung])

  useLayoutEffect(() => {
    const rahmen = liste.current?.parentElement
    if (!fest || !offen || !rahmen) return
    const messen = () => {
      const r = rahmen.getBoundingClientRect()
      const unten = window.innerHeight - r.bottom
      // Unter dem Feld, wenn die Liste (höchstens 14 rem) dort Platz hat — sonst darüber.
      setOrt(unten < 232 && r.top > unten ? { left: r.left, bottom: window.innerHeight - r.top + 4, minWidth: r.width } : { left: r.left, top: r.bottom + 4, minWidth: r.width })
    }
    messen()
    // Rollt die Seite oder die Tabelle, wandert die Liste mit ihrem Feld mit.
    window.addEventListener('scroll', messen, true)
    window.addEventListener('resize', messen)
    return () => {
      window.removeEventListener('scroll', messen, true)
      window.removeEventListener('resize', messen)
    }
  }, [fest, offen])

  if (!offen) return null
  return (
    <div
      ref={liste}
      role="listbox"
      className={`z-50 max-h-56 overflow-auto rounded-md border border-border shadow-lg ${fest ? 'fixed w-max min-w-[16rem] max-w-[min(32rem,calc(100vw-2rem))] text-left' : 'absolute left-0 top-full mt-1 w-full min-w-[16rem]'}`}
      style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))', ...(fest ? (ort ?? { visibility: 'hidden' }) : {}) }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {eintraege.map((t, i) => (
        <button
          key={schluessel(t)}
          type="button"
          role="option"
          aria-selected={i === index}
          tabIndex={-1}
          className={`block w-full px-2 py-1 text-left text-sm hover:bg-muted ${i === index ? 'bg-primary/15' : ''}`}
          onClick={() => onWahl(t, false)}
        >
          {darstellung(t)}
        </button>
      ))}
    </div>
  )
}

/**
 * Verzögerte Suche — leer unter zwei Zeichen.
 *
 * Eine Antwort gilt nur, solange ihre Suche noch die aktuelle ist. Ohne das öffnete eine Antwort, die erst
 * nach dem Verlassen des Felds oder nach dem Weitertippen eintraf, die Vorschlagsliste wieder — mit Treffern
 * zu einem Text, der nicht mehr dasteht, und ohne dass sie je wieder zuging (Auftraggeber 6.10.2026).
 */
export function useSuche<T>(text: string, aktiv: boolean, suche: (q: string) => Promise<T[]>) {
  const [treffer, setTreffer] = useState<T[]>([])
  const sucheRef = useRef(suche)
  sucheRef.current = suche
  useEffect(() => {
    if (!aktiv || text.trim().length < 2) {
      setTreffer([])
      return
    }
    let ueberholt = false
    const t = setTimeout(
      () =>
        sucheRef
          .current(text.trim())
          .then((r) => !ueberholt && setTreffer(r))
          .catch(() => !ueberholt && setTreffer([])),
      250,
    )
    return () => {
      ueberholt = true
      clearTimeout(t)
    }
  }, [text, aktiv])
  return [treffer, setTreffer] as const
}
